import apiRequest from 'api/apiRequest';
import endpoints from 'api/endpoints';
import { AppBundle } from 'api/optimalprint-sdk.d';
import { HttpMethods } from 'api/types';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { Store } from 'store';
import {
  resetCategoriesAction,
  setCategoriesAction,
  setCategoryFiltersAction,
  setCategoryLoadingAction,
  setCategoryRefetchFlagAction,
  setFetchedCategoryIdAction,
} from 'store/category/slice';
import getDesignsFromFamilies from 'utils/family/getDesignsFromFamilies';
import collectPricesFromDesigns from 'utils/price/collectPricesFromDesigns';
import setPricesToFetchOperation from 'store/price/operation/setPricesToFetchOperation';
import { FAMILIES_PER_PAGE } from 'settings';
import prepareCategoryFilters from 'utils/family/prepareCategoryFilters';
import queryStringEncode from 'utils/queryStringEncode';
import getLocaleSelector from 'store/app/selector/getLocaleSelector';
import pageNrToOffset from 'utils/family/pageNrToOffset';
import globalVars from 'utils/globalVars';
import dataFetchToken from 'utils/dataFetchToken';
import isServer from 'utils/isServer';
import getCategoryFilters from '../selector/getCategoryFilters';
import getCategorySortBy from '../selector/getCategorySortBy';
import getCategoryPageNr from '../selector/getCategoryPageNr';
import getTotalFamiliesFetched from '../selector/getTotalFamiliesFetched';
import getFamiliesOffset from '../selector/getFamiliesOffset';
import getCategoryRefetchFlag from '../selector/getCategoryRefetchFlag';
import getCategoryId from '../selector/getCategoryId';
import getTotalFamiliesAvailable from '../selector/getTotalFamiliesAvailable';
import fetchGTMProductsDataOperation from './fetchGTMProductsDataOperation';
import resetGtmProductsDataOperation from './resetGtmProductsDataOperation';

const limit = FAMILIES_PER_PAGE;

const fetchDesignsForPage = async (
  pageNr: number,
  currentDataFetchToken: number,
  store: Store,
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
) => {
  const offset = pageNrToOffset(pageNr);
  const currentCategoryFilters = getCategoryFilters(store);
  const refetchFlag = getCategoryRefetchFlag(store);

  const categories = (await apiRequest(
    endpoints.categoryV3DesignsList,
    {
      locale: getLocaleSelector(store),
      categoryId: getCategoryId(store),
      isMobile: 0,
      offset,
      limit,
      sortBy: getCategorySortBy(store),
    },
    HttpMethods.GET,
    'data',
    currentCategoryFilters ? queryStringEncode({ filters: prepareCategoryFilters(currentCategoryFilters) }) : '',
  )) as AppBundle.Api.Response.Category.V3.CategoryDesignsListV3Response;

  if (currentDataFetchToken !== globalVars.lastCategoryRequestToken) {
    return;
  }
  // collect productId's for later price fetching
  const products = getDesignsFromFamilies(categories.families);
  const prices = collectPricesFromDesigns(products);
  dispatch(setPricesToFetchOperation(prices));

  if (refetchFlag) {
    dispatch(resetCategoriesAction());
    dispatch(setCategoryRefetchFlagAction(false));
    dispatch(resetGtmProductsDataOperation());
  }
  // update filters data all the time
  dispatch(
    setCategoryFiltersAction(
      categories.filters as AppBundle.Api.Response.Category.V3.CategoryDesignsListV3Response.Filters,
    ),
  );

  // Insert design families into structure
  dispatch(setCategoriesAction({ categories, offset }));

  // Request GTM data for designs
  if (!isServer()) {
    dispatch(fetchGTMProductsDataOperation(products.map((product) => product.designIdEncrypted)));
  }
};

const pageIsNotFetched = (
  pageNr: number,
  refetchFlag: boolean,
  leftSegmentLimit: number,
  segmentSize: number,
  pageSize: number,
  totalFamilies: number,
) =>
  (pageNr === 1 && refetchFlag) ||
  (pageNr - 1) * pageSize < leftSegmentLimit ||
  Math.min(pageNr * pageSize, totalFamilies) > leftSegmentLimit + segmentSize;

const fetchDesignsOperation =
  (targetPage?: number) => async (dispatch: ThunkDispatch<Store, undefined, AnyAction>, getState: () => Store) => {
    globalVars.lastCategoryRequestToken = dataFetchToken.next().value;
    let store = getState();
    const totalFamiliesAvailable = getTotalFamiliesAvailable(store) || 999999;

    const currentPageNr = targetPage || getCategoryPageNr(store);
    // Fetching pages
    dispatch(setFetchedCategoryIdAction(getCategoryId(store)));
    dispatch(setCategoryLoadingAction(true));
    if (
      pageIsNotFetched(
        currentPageNr,
        getCategoryRefetchFlag(store),
        getFamiliesOffset(store),
        getTotalFamiliesFetched(store),
        limit,
        totalFamiliesAvailable,
      )
    ) {
      store = getState();
      await fetchDesignsForPage(currentPageNr, globalVars.lastCategoryRequestToken, store, dispatch);
    }
    dispatch(setCategoryLoadingAction(false));
  };

export default fetchDesignsOperation;
